import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { ArrowLeft02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Show } from "shared/components";
import { Button, ButtonSize, ButtonVariant } from "shared/components/Button";
import { cx } from "shared/utils";
import {
  ApplicationStatus,
  crmAnalyticsApi,
  isApplicationStatus,
  type ApplicationPipelineDrilldownItem,
  type ApplicationPipelineStage,
  type ReasonsForDeclineItem
} from "api/crm-analytics";
import { getErrorMessage } from "utils/error";
import { AnalyticsDetailModal } from "./AnalyticsDetailModal";
import { DrilldownRow } from "./DrilldownRow";
import type { CrmDateParams } from "../types";
import type { UnitFilterParams } from "./UnitsFilter";
import {
  DEFAULT_PER_PAGE,
  DRILLDOWN_COLUMNS,
  DRILLDOWN_ONE_DATE_GRID_CLASSNAME,
  DRILLDOWN_TWO_DATE_GRID_CLASSNAME,
  THREE_COLS_DRILLDOWN_GRID_CLASSNAME
} from "../constants";
import { splitStageTransitions } from "../utils";
import { conditionsByAdverseActionLetterType } from "shared/app/MakeDecisionModal/constants";

const REASONS_GRID = "grid grid-cols-[1fr_80px_80px] items-center gap-4";

type AppDrilldownDateConfig = {
  gridClassName: string;
  columns: { label: string }[];
  getDates: (
    item: ApplicationPipelineDrilldownItem
  ) => { value: string | null | undefined }[];
};

const getAppDrilldownDateConfig = (
  fromStageId: string,
  toStageId: string | null
): AppDrilldownDateConfig | null => {
  const isStillIn = fromStageId === toStageId;
  const isStartedOrPartial =
    fromStageId === ApplicationStatus.STARTED ||
    fromStageId === ApplicationStatus.PARTIALLY_SUBMITTED;

  if (isStartedOrPartial && toStageId === ApplicationStatus.SUBMITTED)
    return {
      gridClassName: DRILLDOWN_TWO_DATE_GRID_CLASSNAME,
      columns: [
        { label: "Name" },
        { label: "Property" },
        { label: "Unit" },
        { label: "Date started" },
        { label: "Date submitted" }
      ],
      getDates: (item) => [
        { value: item.started_at },
        { value: item.submitted_at }
      ]
    };

  if (isStillIn && isStartedOrPartial)
    return {
      gridClassName: DRILLDOWN_ONE_DATE_GRID_CLASSNAME,
      columns: [
        { label: "Name" },
        { label: "Property" },
        { label: "Unit" },
        { label: "Date started" }
      ],
      getDates: (item) => [{ value: item.started_at }]
    };

  if (isStillIn && fromStageId === ApplicationStatus.SUBMITTED)
    return {
      gridClassName: DRILLDOWN_ONE_DATE_GRID_CLASSNAME,
      columns: [
        { label: "Name" },
        { label: "Property" },
        { label: "Unit" },
        { label: "Date submitted" }
      ],
      getDates: (item) => [{ value: item.submitted_at }]
    };

  if (isStillIn && fromStageId === ApplicationStatus.CONDITIONALLY_APPROVED)
    return {
      gridClassName: DRILLDOWN_ONE_DATE_GRID_CLASSNAME,
      columns: [
        { label: "Name" },
        { label: "Property" },
        { label: "Unit" },
        { label: "Date approved" }
      ],
      getDates: (item) => [{ value: item.decision_at }]
    };

  return null;
};

const declineReasonTextById: Record<string, string> = Object.values(
  conditionsByAdverseActionLetterType
)
  .flat()
  .reduce<Record<string, string>>((acc, { id, text }) => {
    acc[id] = text;
    return acc;
  }, {});

const formatDeclineReason = (reason: string): string =>
  declineReasonTextById[reason] ?? reason;
const NOOP_PAGINATION = {
  total: 0,
  current: 1,
  onChange: () => undefined
};

type BreakdownRow = {
  key: string;
  label: string;
  title: string;
  count: number;
  pct: number;
  fromStageId: string;
  toStageId: string | null;
};

type ModalMode = "decline" | "drilldown" | "breakdown";

type Pagination = {
  total: number;
  current: number;
  onChange: (page: number) => void;
};

const getModalMode = (
  isDeclineStage: boolean,
  hasSelectedRow: boolean
): ModalMode => {
  if (isDeclineStage) return "decline";
  if (hasSelectedRow) return "drilldown";
  return "breakdown";
};

const getModalTitle = (
  mode: ModalMode,
  drilldownTitle: string,
  stageShortLabel: string
): string => {
  if (mode === "decline") return "Reasons for decline";
  if (mode === "drilldown") return drilldownTitle;
  return stageShortLabel;
};

const getModalIsEmpty = (
  mode: ModalMode,
  declineCount: number,
  leadsCount: number,
  breakdownCount: number
): boolean => {
  if (mode === "decline") return declineCount === 0;
  if (mode === "drilldown") return leadsCount === 0;
  return breakdownCount === 0;
};

const safePercent = (raw: string): number => {
  const n = Number(raw);
  return Number.isFinite(n) ? Math.round(n) : 0;
};

const buildBreakdownRows = (
  stage: ApplicationPipelineStage
): BreakdownRow[] => {
  if (!isApplicationStatus(stage.stage_id)) return [];

  const { stillInStage, movements, base } = splitStageTransitions(stage);

  const rows: BreakdownRow[] = movements.flatMap((m) => {
    const isOther = m.stage_id === null;
    if (!isOther && !isApplicationStatus(m.stage_id ?? "")) return [];
    const keySuffix = m.stage_id ?? `other-${m.stage_name}`;
    return [
      {
        key: `moved-${keySuffix}`,
        label: `${m.count} Moved into ${m.stage_name}`,
        title: `Moved into ${m.stage_name}`,
        count: m.count,
        pct: Math.round((m.count / base) * 100),
        fromStageId: stage.stage_id,
        toStageId: m.stage_id
      }
    ];
  });

  if (stillInStage && stillInStage.count > 0)
    rows.push({
      key: "still",
      count: stillInStage.count,
      label: `${stillInStage.count} Still in ${stage.stage_name}`,
      title: `Still in ${stage.stage_name}`,
      pct: Math.round((stillInStage.count / base) * 100),
      fromStageId: stage.stage_id,
      toStageId: stage.stage_id
    });

  return rows;
};

const BreakdownRowView = ({
  row,
  onClick
}: {
  row: BreakdownRow;
  onClick?: () => void;
}) => (
  <div
    className={cx(
      "flex items-center gap-3 px-5 py-3 bg-content-1000-inverted border-b border-alpha-40",
      onClick ? "cursor-pointer hover:bg-alpha-20" : ""
    )}
    onClick={onClick}
  >
    <p className="flex-1 text-sm font-medium text-content-700 truncate">
      {row.label}
    </p>
    <span className="text-xs font-bold text-content-1000 bg-alpha-40 px-2 py-0.5 rounded-full shrink-0 w-12 text-center">
      {row.pct}%
    </span>
  </div>
);

const ReasonRowView = ({ item }: { item: ReasonsForDeclineItem }) => (
  <div
    className={cx(
      REASONS_GRID,
      "px-5 py-3 bg-content-1000-inverted border-b border-alpha-40"
    )}
  >
    <p className="text-sm font-medium text-content-700 truncate">
      {formatDeclineReason(item.reason)}
    </p>
    <p className="text-sm font-medium text-content-700">{item.count}</p>
    <span className="text-xs font-bold text-content-1000 bg-alpha-40 px-2 py-0.5 rounded-full shrink-0 w-14 text-center">
      {safePercent(item.percentage)}%
    </span>
  </div>
);

export const ApplicationPipelineModal = ({
  opened,
  onClose,
  stage,
  dateParams,
  filterParams
}: {
  opened: boolean;
  onClose: () => void;
  stage: ApplicationPipelineStage | null;
  dateParams: CrmDateParams;
  filterParams?: UnitFilterParams;
}) => {
  const navigate = useNavigate();
  const [selectedRow, setSelectedRow] = useState<BreakdownRow | null>(null);
  const [page, setPage] = useState(1);
  const [declinePage, setDeclinePage] = useState(1);

  const isDeclineStage =
    stage != null && stage.stage_id === ApplicationStatus.DECLINED;

  const handleApplicationClick = (applicationId: number) => {
    onClose();
    navigate(`/applications/${applicationId}`);
  };

  useEffect(() => {
    setPage(1);
    setDeclinePage(1);
    setSelectedRow(null);
  }, [opened, dateParams, filterParams]);

  useEffect(() => {
    setPage(1);
  }, [selectedRow]);

  const breakdownRows =
    stage && !isDeclineStage ? buildBreakdownRows(stage) : [];

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "CRM_ANALYTICS_APPLICATION_PIPELINE_DRILLDOWN",
      selectedRow?.fromStageId,
      selectedRow?.toStageId,
      dateParams,
      filterParams,
      page
    ],
    queryFn: () =>
      crmAnalyticsApi.getApplicationPipelineDrilldown({
        from_status: selectedRow!.fromStageId as ApplicationStatus,
        to_status: selectedRow!.toStageId as ApplicationStatus,
        ...dateParams,
        ...filterParams,
        page,
        per_page: DEFAULT_PER_PAGE
      }),
    enabled:
      opened &&
      selectedRow !== null &&
      selectedRow.toStageId !== null &&
      !isDeclineStage,
    placeholderData: keepPreviousData,
    meta: {
      onError: (err: unknown) => toast.error(getErrorMessage(err))
    }
  });

  const {
    data: declineData,
    isLoading: isDeclineLoading,
    isFetching: isDeclineFetching
  } = useQuery({
    queryKey: [
      "CRM_ANALYTICS_REASONS_FOR_DECLINE",
      dateParams,
      filterParams,
      declinePage
    ],
    queryFn: () =>
      crmAnalyticsApi.getReasonsForDecline({
        ...filterParams,
        ...dateParams,
        page: declinePage,
        per_page: DEFAULT_PER_PAGE
      }),
    enabled: opened && isDeclineStage && (stage?.total_count ?? 0) > 0,
    placeholderData: keepPreviousData,
    meta: {
      onError: (err: unknown) => toast.error(getErrorMessage(err))
    }
  });

  const leads = data?.items ?? [];
  const totalPages = data?.page_count ?? 0;
  const declineItems = declineData?.items ?? [];
  const declineTotalPages = declineData?.page_count ?? 0;

  if (!stage) return null;

  const isDrillDown = selectedRow !== null;
  const mode = getModalMode(isDeclineStage, isDrillDown);
  const stageShortLabel = stage.stage_name;
  const isDrilldownMode = mode === "drilldown";
  const isDeclineMode = mode === "decline";
  const isBreakdownMode = mode === "breakdown";

  const title = getModalTitle(
    mode,
    selectedRow?.title ?? stageShortLabel,
    stageShortLabel
  );

  const dateConfig = selectedRow
    ? getAppDrilldownDateConfig(selectedRow.fromStageId, selectedRow.toStageId)
    : null;
  const gridClassName = isDeclineMode
    ? REASONS_GRID
    : (dateConfig?.gridClassName ?? THREE_COLS_DRILLDOWN_GRID_CLASSNAME);
  const columns = dateConfig?.columns ?? DRILLDOWN_COLUMNS;
  const showColumns = isDrilldownMode;
  const showPagination = isDrilldownMode || isDeclineMode;
  const panelClassName = isBreakdownMode ? "max-w-[707px]" : undefined;

  const modalLoading =
    (isDrilldownMode && isLoading) || (isDeclineMode && isDeclineLoading);
  const modalFetching =
    (isDrilldownMode && isFetching) || (isDeclineMode && isDeclineFetching);

  const drilldownPagination = {
    total: totalPages,
    current: page,
    onChange: setPage
  };
  const declinePagination = {
    total: declineTotalPages,
    current: declinePage,
    onChange: setDeclinePage
  };
  let pagination: Pagination = NOOP_PAGINATION;
  if (isDrilldownMode) pagination = drilldownPagination;
  else if (isDeclineMode) pagination = declinePagination;

  const isEmpty = getModalIsEmpty(
    mode,
    declineItems.length,
    leads.length,
    breakdownRows.length
  );

  return (
    <AnalyticsDetailModal
      opened={opened}
      onClose={onClose}
      title={title}
      gridClassName={gridClassName}
      columns={columns}
      isLoading={modalLoading}
      isFetching={modalFetching}
      isEmpty={isEmpty}
      emptyText="No data to show"
      pagination={pagination}
      showColumns={showColumns}
      showPagination={showPagination}
      headerExtra={
        <Show if={isDrilldownMode}>
          <Button
            variant={ButtonVariant.TEXT}
            size={ButtonSize.XS}
            HugeIcon={ArrowLeft02Icon}
            className="flex items-center gap-2"
            onClick={() => setSelectedRow(null)}
          >
            Back to breakdown
          </Button>
        </Show>
      }
      panelClassName={panelClassName}
    >
      <Show if={isDeclineMode}>
        {declineItems.map((item) => (
          <ReasonRowView key={item.reason} item={item} />
        ))}
      </Show>
      <Show if={isBreakdownMode}>
        <div>
          {breakdownRows.map((row) => (
            <BreakdownRowView
              key={row.key}
              row={row}
              onClick={
                row.toStageId === null ? undefined : () => setSelectedRow(row)
              }
            />
          ))}
        </div>
      </Show>
      <Show if={isDrilldownMode}>
        {leads.map((item) => (
          <DrilldownRow
            key={item.id}
            name={item.name}
            propertyName={item.property?.name ?? null}
            unitTitle={item.unit?.title ?? null}
            onClick={() => handleApplicationClick(item.id)}
            gridClassName={dateConfig?.gridClassName}
            dates={dateConfig?.getDates(item)}
          />
        ))}
      </Show>
    </AnalyticsDetailModal>
  );
};
